import { useEffect, useId, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Tooltip } from '@/components/Tooltip/Tooltip';
import { MathText } from '@/features/content/MathText';

import styles from './EquationModal.module.css';

interface EquationModalProps {
  initialValue?: string;
  isOpen: boolean;
  onClose: () => void;
  onInsert: (latex: string) => void;
  /**
   * Called once the dialog has gone, with how it closed. Nothing opens it
   * through a Radix trigger, so Radix cannot give focus back by itself: the
   * editor puts it in the text after an insert, and on the button that opened
   * the dialog otherwise.
   */
  onClosed?: (outcome: 'dismissed' | 'inserted') => void;
}

/**
 * The equation dialog.
 *
 * A Radix dialog, in a portal over the whole page. It used to be a box drawn
 * inside the editor, under the app's header, the chapter sidebar and the
 * page's Save bar, all of which stayed clickable, with focus free to leave it
 * and its Insert button below the fold of an 800 px window.
 */
export function EquationModal({
  initialValue = '',
  isOpen,
  onClose,
  onClosed,
  onInsert,
}: EquationModalProps) {
  const [latex, setLatex] = useState(initialValue);
  const inputId = useId();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const outcomeRef = useRef<'dismissed' | 'inserted'>('dismissed');
  const trimmed = latex.trim();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    outcomeRef.current = 'dismissed';
    setLatex(initialValue);
  }, [initialValue, isOpen]);

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      open={isOpen}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          aria-describedby={undefined}
          className={styles.content}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onClosed?.(outcomeRef.current);
          }}
          onOpenAutoFocus={(event) => {
            // Straight to the field, rather than the close button that comes
            // first in the dialog.
            event.preventDefault();
            textareaRef.current?.focus();
          }}
        >
          <header className={styles.header}>
            <div>
              <p className={styles.eyebrow}>Equation helper</p>
              <Dialog.Title className={styles.title}>Insert equation</Dialog.Title>
            </div>
            <Tooltip content="Close equation dialog">
              <Dialog.Close asChild>
                <button
                  aria-label="Close equation dialog"
                  className={styles.closeButton}
                  type="button"
                >
                  ×
                </button>
              </Dialog.Close>
            </Tooltip>
          </header>

          {/* Only this part scrolls, so Cancel and Insert stay in view however
              short the window is. */}
          <div className={styles.body}>
            <label className={styles.label} htmlFor={inputId}>
              LaTeX
            </label>
            <textarea
              className={styles.textarea}
              id={inputId}
              onChange={(event) => {
                setLatex(event.target.value);
              }}
              placeholder="e.g. \frac{F}{m}"
              ref={textareaRef}
              rows={4}
              value={latex}
            />

            <div className={styles.preview}>
              <p className={styles.previewLabel}>Preview</p>
              <div className={styles.previewSurface}>
                {trimmed.length > 0 ? (
                  <MathText text={`$$${trimmed}$$`} />
                ) : (
                  <span className={styles.previewPlaceholder}>
                    Enter LaTeX to preview the equation.
                  </span>
                )}
              </div>
            </div>
          </div>

          <footer className={styles.actions}>
            <Dialog.Close asChild>
              <button className={styles.secondaryAction} type="button">
                Cancel
              </button>
            </Dialog.Close>
            <button
              className={styles.primaryAction}
              disabled={trimmed.length === 0}
              onClick={() => {
                outcomeRef.current = 'inserted';
                onInsert(trimmed);
              }}
              type="button"
            >
              Insert
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
